import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { SessionStorageService } from '../services/session-storage.service';
import {
  FuelConnection,
  FuelConsumptionReportRow,
  FuelDashboard,
  FuelProviderApp,
  FuelSyncRun,
  FuelTransaction,
  FuelTransactionFilters
} from './fuel-management.model';

@Injectable({ providedIn: 'root' })
export class FuelManagementService {
  private readonly http = inject(HttpClient);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly baseUrl = `${environment.restApiUrl}fuel-management`;

  getCompanyId(): number {
    return this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
  }

  getCustomerId(): number {
    return this.sessionStorageService.getCustomer()?.id || 0;
  }

  marketplaceApps(): Observable<FuelProviderApp[]> {
    return this.http.get<APIResponseWrapper<FuelProviderApp[]>>(`${this.baseUrl}/marketplace/apps`)
      .pipe(map(response => response.data || []));
  }

  connections(companyId = this.getCompanyId()): Observable<FuelConnection[]> {
    return this.http.get<APIResponseWrapper<FuelConnection[]>>(`${this.baseUrl}/connections`, {
      params: new HttpParams().set('owningCompanyId', companyId)
    }).pipe(map(response => response.data || []));
  }

  saveConnection(connection: FuelConnection): Observable<FuelConnection> {
    const payload: FuelConnection = {
      ...connection,
      owningCompanyId: connection.owningCompanyId || this.getCompanyId()
    };
    const request = payload.connectionId
      ? this.http.put<APIResponseWrapper<FuelConnection>>(`${this.baseUrl}/connections/${payload.connectionId}`, payload)
      : this.http.post<APIResponseWrapper<FuelConnection>>(`${this.baseUrl}/connections`, payload);
    return request.pipe(map(response => response.data));
  }

  testConnection(connectionId: number, companyId = this.getCompanyId()): Observable<FuelSyncRun> {
    const params = new HttpParams()
      .set('owningCompanyId', companyId)
      .set('requestedByCustomerId', this.getCustomerId());
    return this.http.post<APIResponseWrapper<FuelSyncRun>>(`${this.baseUrl}/connections/${connectionId}/test`, {}, { params })
      .pipe(map(response => response.data));
  }

  syncConnection(connectionId: number, companyId = this.getCompanyId()): Observable<FuelSyncRun> {
    const params = new HttpParams()
      .set('owningCompanyId', companyId)
      .set('requestedByCustomerId', this.getCustomerId());
    return this.http.post<APIResponseWrapper<FuelSyncRun>>(`${this.baseUrl}/connections/${connectionId}/sync`, {}, { params })
      .pipe(map(response => response.data));
  }

  importCsv(file: File, connectionId?: number | null, providerCode?: string | null, companyId = this.getCompanyId()): Observable<FuelSyncRun> {
    return this.importFuelFile('csv', file, connectionId, providerCode, companyId);
  }

  importReport(file: File, connectionId?: number | null, providerCode?: string | null, companyId = this.getCompanyId()): Observable<FuelSyncRun> {
    return this.importFuelFile('report', file, connectionId, providerCode, companyId);
  }

  private importFuelFile(importType: 'csv' | 'report', file: File, connectionId?: number | null,
                         providerCode?: string | null, companyId = this.getCompanyId()): Observable<FuelSyncRun> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('owningCompanyId', String(companyId));
    formData.append('requestedByCustomerId', String(this.getCustomerId()));
    if (connectionId) {
      formData.append('connectionId', String(connectionId));
    }
    if (providerCode) {
      formData.append('providerCode', providerCode);
    }
    return this.http.post<APIResponseWrapper<FuelSyncRun>>(`${this.baseUrl}/imports/${importType}`, formData)
      .pipe(map(response => response.data));
  }

  transactions(filters: FuelTransactionFilters = {}, companyId = this.getCompanyId()): Observable<APIListWrapper<FuelTransaction>> {
    let params = new HttpParams()
      .set('owningCompanyId', companyId)
      .set('page', filters.page ?? 0)
      .set('pageSize', filters.pageSize ?? 50);
    params = this.appendParam(params, 'providerCode', filters.providerCode);
    params = this.appendParam(params, 'reconciliationStatus', filters.reconciliationStatus);
    params = this.appendParam(params, 'vehicleId', filters.vehicleId);
    params = this.appendDateParam(params, 'fromDate', filters.fromDate);
    params = this.appendDateParam(params, 'toDate', filters.toDate);
    return this.http.get<APIListWrapper<FuelTransaction>>(`${this.baseUrl}/transactions`, { params });
  }

  dashboard(fromDate?: Date | string | null, toDate?: Date | string | null, companyId = this.getCompanyId()): Observable<FuelDashboard> {
    let params = new HttpParams().set('owningCompanyId', companyId);
    params = this.appendDateParam(params, 'fromDate', fromDate);
    params = this.appendDateParam(params, 'toDate', toDate);
    return this.http.get<APIResponseWrapper<FuelDashboard>>(`${this.baseUrl}/dashboard`, { params })
      .pipe(map(response => response.data || {}));
  }

  consumptionReport(fromDate?: Date | string | null, toDate?: Date | string | null, companyId = this.getCompanyId()): Observable<FuelConsumptionReportRow[]> {
    let params = new HttpParams().set('owningCompanyId', companyId);
    params = this.appendDateParam(params, 'fromDate', fromDate);
    params = this.appendDateParam(params, 'toDate', toDate);
    return this.http.get<APIResponseWrapper<FuelConsumptionReportRow[]>>(`${this.baseUrl}/reports/consumption`, { params })
      .pipe(map(response => response.data || []));
  }

  runs(companyId = this.getCompanyId()): Observable<FuelSyncRun[]> {
    return this.http.get<APIResponseWrapper<FuelSyncRun[]>>(`${this.baseUrl}/runs`, {
      params: new HttpParams().set('owningCompanyId', companyId)
    }).pipe(map(response => response.data || []));
  }

  reconcile(transactionId: number, request: { vehicleId?: number; vehiclePlateNumber?: string; driverId?: number }): Observable<FuelTransaction> {
    return this.http.post<APIResponseWrapper<FuelTransaction>>(`${this.baseUrl}/transactions/${transactionId}/reconcile`, {
      ...request,
      reconciledByCustomerId: this.getCustomerId()
    }).pipe(map(response => response.data));
  }

  ignore(transactionId: number, ignoredReason: string): Observable<FuelTransaction> {
    return this.http.post<APIResponseWrapper<FuelTransaction>>(`${this.baseUrl}/transactions/${transactionId}/ignore`, {
      ignoredReason,
      ignoredByCustomerId: this.getCustomerId()
    }).pipe(map(response => response.data));
  }

  private appendParam(params: HttpParams, key: string, value: any): HttpParams {
    if (value === null || value === undefined || String(value).trim() === '') {
      return params;
    }
    return params.set(key, value);
  }

  private appendDateParam(params: HttpParams, key: string, value?: Date | string | null): HttpParams {
    if (!value) {
      return params;
    }
    return params.set(key, value instanceof Date ? value.toISOString() : value);
  }
}
